/**
 * API service utilities for DocScout application
 * Simplified version for code review - only includes project APIs and prompt generation
 */
import SentryRequestHandler from '@amzn/sentry-fetch';

/**
 * Initialize authentication system
 * This function sets up the authentication context for the application
 */
export const initializeAuth = async (): Promise<void> => {
  try {
    console.log('[Auth Init] Initializing authentication system...');
    
    // For Sentry-based authentication, we don't need to do much initialization
    // The authentication happens automatically when making API requests
    // We can optionally test the authentication by making a simple API call
    
    // Test authentication by checking if we can access the API
    const baseUrl = getApiBaseUrl();
    console.log(`[Auth Init] Testing authentication with base URL: ${baseUrl}`);
    
    // Make a simple test request to verify authentication works
    // This will trigger Midway authentication if needed
    try {
      await sentryApiRequest('/health', { method: 'GET' });
      console.log('[Auth Init] Authentication test successful');
    } catch (error) {
      // Don't fail initialization if health check fails - the API might not have a health endpoint
      console.warn('[Auth Init] Health check failed, but continuing initialization:', error);
    }
    
    console.log('[Auth Init] Authentication initialization completed');
  } catch (error) {
    console.error('[Auth Init] Authentication initialization failed:', error);
    // Don't throw the error - let the app continue even if auth init fails
    // The actual authentication will happen when API calls are made
  }
};

// API Configuration
const API_CONFIG = {
  // Development API endpoint - update this with your actual API Gateway URL after CDK deployment
  DEV_BASE_URL: 'https://docscout-dev-api.dba.legal.amazon.dev',
  // Same-site API design: Frontend and API use separate subdomains
  Frontend: 'https://docscout-{stage}.dba.legal.amazon.dev',
  API: 'https://docscout-{stage}-api.dba.legal.amazon.dev',
  // Production uses a different domain structure (no stage suffix, legal.amazon.dev parent)
  PROD_BASE_URL: 'https://docscout-api.legal.amazon.dev',
  TIMEOUT: 30000, // 30 seconds
};

/**
 * Get the appropriate API base URL based on environment
 */
export const getApiBaseUrl = (): string => {
  if (typeof window === 'undefined') {
    return API_CONFIG.DEV_BASE_URL;
  }
  
  const hostname = window.location.hostname;
  console.log(`[API Config] Hostname: ${hostname}`);
  
  // Local development - MUST come first to avoid falling through to production
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0') {
    console.log(`[API Config] Using DEV_BASE_URL for localhost: ${API_CONFIG.DEV_BASE_URL}`);
    return API_CONFIG.DEV_BASE_URL;
  }
  
  // Production environment: docscout.legal.amazon.dev → docscout-api.legal.amazon.dev
  if (hostname === 'docscout.legal.amazon.dev') {
    console.log(`[API Config] Using production API domain: ${API_CONFIG.PROD_BASE_URL}`);
    return API_CONFIG.PROD_BASE_URL;
  }
  
  // Same-site API design for dev/beta environments
  // Frontend: https://docscout-{stage}.dba.legal.amazon.dev
  // API: https://docscout-{stage}-api.dba.legal.amazon.dev
  if (hostname.includes('.dba.legal.amazon.dev')) {
    // Extract stage from hostname (e.g., docscout-dev -> dev)
    const match = hostname.match(/docscout-(.+)\.dba\.legal\.amazon\.dev/);
    const stage = match ? match[1] : 'dev';
    const apiDomain = `https://docscout-${stage}-api.dba.legal.amazon.dev`;
    console.log(`[API Config] Using same-site API domain: ${apiDomain}`);
    return apiDomain;
  }
  
  // Default to dev for other environments
  console.log(`[API Config] Using DEV_BASE_URL as default: ${API_CONFIG.DEV_BASE_URL}`);
  return API_CONFIG.DEV_BASE_URL;
};

/**
 * Sentry-based API request function for authenticated requests
 * This uses @amzn/sentry-fetch which automatically handles Midway authentication
 */
export const sentryApiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${endpoint}`;
  
  // Default options for Sentry requests
  const defaultOptions: RequestInit & { sentryOptions?: any } = {
    headers: {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    },
    credentials: 'include', // This ensures cookies are sent with cross-origin requests
    mode: 'cors', // Explicitly set CORS mode
    sentryOptions: {
      preserveRedirectUri: true,
      debug: true,
      followMidwayStepUp: true,
      followMidwayStepUpOptions: {
        mode: 'confirm',
        message: 'You need to authenticate with Midway to access this API. You will be redirected to the login page and then back to this application.'
      }
    },
    ...options,
  };
  
  // Log request details for debugging
  console.log(`[Sentry API Request] ${options.method || 'GET'} ${url}`);
  console.log(`[Sentry API Request] Headers:`, defaultOptions.headers);
  console.log(`[Sentry API Request] Credentials: ${defaultOptions.credentials}`);
  console.log(`[Sentry API Request] Sentry Options:`, defaultOptions.sentryOptions);
  
  try {
    // Use Sentry fetch for authenticated requests
    const response = await SentryRequestHandler(url, defaultOptions);
    
    console.log(`[Sentry API Response] Status: ${response.status}`);
    
    // Convert headers to object for logging (compatible with older TypeScript)
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });
    console.log(`[Sentry API Response] Headers:`, responseHeaders);
    
    // Handle authentication errors
    if (response.status === 401) {
      console.warn('Sentry authentication failed, user may need to log in');
      throw new Error('Authentication required - please log in to Midway');
    }
    
    // Handle Midway authentication errors
    if (response.status === 403) {
      const errorText = await response.text().catch(() => '');
      console.error('Midway authentication failed:', errorText);
      
      if (errorText.includes('MissingAuthenticationToken')) {
        throw new Error('Midway authentication token missing - please log in');
      }
      
      throw new Error(`Authentication failed: ${response.status} ${response.statusText}`);
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Sentry API request failed for ${endpoint}:`, error);
    throw error;
  }
};
